// Auth context for TagBoard web.
//
// Wraps the Supabase auth client in a React context so the entire tree can
// read the current session + user without prop-drilling. AuthProvider is
// mounted once at the top of App.tsx; useAuth() is callable from anywhere
// underneath.
//
// Persistence is handled by Supabase JS itself (see supabaseClient.ts —
// `persistSession: true` writes the session to localStorage). After a hard
// refresh, `supabase.auth.getSession()` resolves to the cached session and
// the AuthGate skips the LoginScreen.
//
// v1 surface: email + password sign-in. signOut + onAuthStateChange handle
// session lifecycle. Sign-up is admin-only (no signUp helper exposed).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import type {Session, User} from '@supabase/supabase-js'
import {supabase} from './supabaseClient'

/**
 * The application-level profile sitting on top of the bare auth.user.
 * Loaded from public.users + the linked personnel row (if any) after sign-in.
 * Header + future role-aware UI read from this, NOT from user.email.
 */
export interface UserProfile {
  id: string
  display_name: string | null
  role: 'admin' | 'dispatcher' | 'supervisor' | 'worker'
  personnel_id: string | null
  personnel_name: string | null
}

interface AuthContextValue {
  session: Session | null
  user: User | null
  profile: UserProfile | null
  /** Until the initial getSession() resolves we don't know if a session
   *  exists — the AuthGate shows a Loading state during this window. */
  loading: boolean
  signIn: (email: string, password: string) => Promise<{error?: string}>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    // Resolve the cached session on mount. If the stored refresh token is
    // dead (revoked, expired, or wiped server-side after a JWT-secret
    // rotation) supabase-js throws AuthApiError("Invalid Refresh Token:
    // Refresh Token Not Found"). Clear the local copy so AuthGate drops
    // the user back to LoginScreen instead of the console spam loop on
    // every auto-refresh tick.
    ;(async () => {
      try {
        const {data, error} = await supabase.auth.getSession()
        if (cancelled) return
        if (error) {
          console.warn('[Auth] Stale session, signing out locally:', error.message)
          await supabase.auth.signOut({scope: 'local'})
          setSession(null)
        } else {
          setSession(data.session)
        }
      } catch (err: any) {
        console.warn('[Auth] Session resolve failed:', err?.message ?? err)
        await supabase.auth.signOut({scope: 'local'}).catch(() => {})
        if (!cancelled) setSession(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    // Subscribe to subsequent SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED events.
    // If TOKEN_REFRESHED arrives with a null session, the auto-refresher gave
    // up — wipe the local copy so we don't keep retrying a dead token.
    const {data: sub} = supabase.auth.onAuthStateChange((event, next) => {
      if (event === 'TOKEN_REFRESHED' && !next) {
        supabase.auth.signOut({scope: 'local'}).catch(() => {})
      }
      setSession(next)
    })
    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  // Load the application profile (public.users + linked personnel.name)
  // whenever the active user changes. Falls back to a minimal profile when
  // no public.users row exists yet so the Header still has something to
  // render — admin can backfill the row later without breaking the UI.
  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) {
      setProfile(null)
      return
    }
    let cancelled = false
    ;(async () => {
      // PostgREST embed: `personnel:personnel_id(name)` joins via the FK
      // and returns the personnel row nested under `personnel`.
      const {data, error} = await supabase
        .from('users')
        .select('id, display_name, role, personnel_id, personnel:personnel_id(name)')
        .eq('id', userId)
        .maybeSingle()
      if (cancelled) return
      if (error || !data) {
        // No row yet — graceful fallback so the Header / Auth gate still work.
        setProfile({
          id: userId,
          display_name: null,
          role: 'dispatcher',
          personnel_id: null,
          personnel_name: null,
        })
        return
      }
      // Supabase returns the embedded personnel as an object OR an array
      // depending on the relationship cardinality — normalize.
      const personnel = Array.isArray((data as any).personnel)
        ? (data as any).personnel[0]
        : (data as any).personnel
      setProfile({
        id: data.id,
        display_name: (data.display_name as string | null) ?? null,
        role: ((data.role as string) ?? 'dispatcher') as UserProfile['role'],
        personnel_id: (data.personnel_id as string | null) ?? null,
        personnel_name: personnel?.name ?? null,
      })
    })()
    return () => {
      cancelled = true
    }
  }, [session?.user?.id])

  const signIn = useCallback(
    async (email: string, password: string): Promise<{error?: string}> => {
      const {error} = await supabase.auth.signInWithPassword({email, password})
      if (error) return {error: error.message}
      // session is set via onAuthStateChange — no need to setSession here.
      return {}
    },
    [],
  )

  const signOut = useCallback(async () => {
    // Defensive: clear state immediately AND call Supabase. The
    // onAuthStateChange listener *should* set session→null after the
    // signOut() resolves, but if anything in that path fails (network
    // error, stale subscription, HMR-orphaned listener) the UI gets stuck
    // showing the logged-in shell. Setting state here guarantees the gate
    // re-renders.
    try {
      await supabase.auth.signOut()
    } catch (err) {
      // Swallow — we still want the local session cleared even if the
      // server-side revoke fails (e.g. offline). The next session refresh
      // will fail and force a re-login.
      console.warn('[Auth] signOut error (clearing local state anyway):', err)
    }
    setSession(null)
    setProfile(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
